import moment from "moment";
import "moment/locale/es";
import { saveAs } from "file-saver";
import * as XLSXStyle from "xlsx-js-style";
import {
  FORMATO_CONTABILIDAD,
  border,
  headerStyle,
  celdaTexto,
  celdaMoneda,
  nombreEmpresa,
  direccionEmpresa,
  contactoEmpresa,
} from "../../../../Components/ExcelEstilos";
import { RESALTES, construirLibroBlob } from "../../../../Components/ExcelTablaReporte";
import inyectarLogoEnExcel from "../../../../Components/ExcelLogo";
import cuentaMask from "../../../../Components/cuentaMask";
import round2Decimals from "../../../../Components/round2Decimals";
import {
  opcionesPdfBalance,
  traeSubtotales,
  traeAnalisis,
  filtrarSinSaldo,
  marcarJerarquia,
  agruparPorSubtipo,
  construirAnaliticas,
  renglonesResumen,
  renglonesRazones,
} from "../../../../Pdf/balanceGeneralAnaliticas";

moment.locale("es");

/*
  Excel del Estado de Posicion Financiera (Balance General).

  Es el gemelo del PDF clasico (`Pdf/reporteDeEstadoDePosicionFinancieraPdf.js`):
  Activo a la izquierda, Pasivo y Capital a la derecha, el mismo arbol de cuentas
  y las mismas sumas. Las tres variantes son las claves clasicas del catalogo de
  `balanceGeneralAnaliticas`, asi que "con subtotales" agrupa igual que el PDF.
  La unica diferencia: el analisis no baja como un segundo archivo sino como una
  segunda hoja del mismo libro.

  `construirHojasBalance` es pura (se puede probar en node); `descargarExcelBalance`
  es la que toca el navegador (logo y descarga).
*/

/** Las tres variantes del menu de Excel; solo cambia el texto del analisis. */
export const OPCIONES_EXCEL_BALANCE = opcionesPdfBalance(false).map((tipo) =>
  tipo.analisis
    ? { ...tipo, descripcion: "Subtotales + una hoja de análisis con % y razones" }
    : tipo
);

// ── Paleta ──────────────────────────────────────────────────────────────────
const TEAL_BANDA = "007A8A";
const TEAL_TEXTO = "00505C";
const ROJO_NEGATIVO = "CF1322";

// A Cuenta · B Descripcion · C Importe · D separador · E Cuenta · F Descripcion · G Importe
const ANCHOS = [20, 42, 18, 2, 20, 42, 18];
const COL_DERECHA = 4;

// El logo se ancla en A1 y se encaja en esta caja; los renglones del encabezado
// tienen que sumar mas alto que ella o Excel lo recorta. La caja es mas angosta
// que la de omision (200 px) porque la columna A es la de la Cuenta.
const FILAS_ENCABEZADO = 4;
const ALTO_ENCABEZADO = 16;
const LOGO = { anchoMax: 120, altoMax: 80 };

// ── Estilos ─────────────────────────────────────────────────────────────────
// El encabezado del documento no lleva la reticula de la tabla: va sin `border`.
const tituloEmpresaStyle = {
  font: { bold: true, sz: 14 },
  alignment: { horizontal: "center", vertical: "center" },
};
const datosEmpresaStyle = { font: { sz: 10 }, alignment: { horizontal: "center", vertical: "center" } };
const metaStyle = {
  font: { sz: 9, color: { rgb: "8C8C8C" } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
};
const fechaCorteStyle = {
  font: { bold: true, sz: 11, color: { rgb: "005F6B" } },
  alignment: { horizontal: "center", vertical: "center" },
};
const bandaTituloStyle = {
  font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: TEAL_BANDA } },
  alignment: { horizontal: "center", vertical: "center" },
};

// Tabla
const bandaSeccionStyle = {
  font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: TEAL_BANDA } },
  alignment: { horizontal: "center", vertical: "center" },
  border,
};
const tituloGrupoStyle = {
  font: { bold: true, sz: 10, color: { rgb: TEAL_TEXTO } },
  fill: { fgColor: { rgb: "BFEFF5" } },
  alignment: { horizontal: "center", vertical: "center" },
  border,
};
const pieStyle = {
  font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: TEAL_BANDA } },
  border,
};

/**
 * Fuente y relleno de una cuenta segun su lugar en el arbol. Mismas tres senas
 * que el PDF (peso, tinte y color), con los tonos de la paleta Biwo: los padres
 * de los dos primeros niveles llevan fondo, los profundos solo el teal, y las
 * hojas van en tinta normal porque son el dato, no la estructura.
 */
const tonoCuenta = (fila) => {
  if (!fila.esPadre) return { font: { sz: 10, color: { rgb: "333333" } } };
  if (fila.profundidad === 1) {
    return {
      font: { bold: true, sz: 10, color: { rgb: TEAL_TEXTO } },
      fill: { fgColor: { rgb: "D6F4F8" } },
    };
  }
  if (fila.profundidad === 2) {
    return {
      font: { bold: true, sz: 10, color: { rgb: "1F2937" } },
      fill: { fgColor: { rgb: "F0FEFF" } },
    };
  }
  return { font: { bold: true, sz: 10, color: { rgb: TEAL_TEXTO } } };
};

const alinear = (horizontal, extra = {}) => ({ horizontal, vertical: "center", ...extra });

/** Tinta roja encima del tono, para los importes en negativo. */
const enRojo = (tono) => ({
  ...tono,
  font: { ...(tono.font || {}), color: { rgb: ROJO_NEGATIVO } },
});

// ── Celdas ──────────────────────────────────────────────────────────────────
const vacia = () => ({ v: "" });
const tripleVacio = () => [vacia(), vacia(), vacia()];

/**
 * Importe redondeado a centavos, en formato Contabilidad y como numero real.
 * Se redondea para que lo que suma Excel sea lo que se ve, sin el residuo de
 * punto flotante que deja restar cargos contra abonos.
 */
const celdaImporte = (valor, tono) => {
  const importe = round2Decimals(Number(valor) || 0);
  const tinta = importe < 0 ? enRojo(tono) : tono;
  return celdaMoneda(importe, {
    ...tinta,
    border,
    alignment: alinear("right"),
    numFmt: FORMATO_CONTABILIDAD,
  });
};

// nivel1..nivel5 son String con default null en el modelo: hay que blindar el concat.
const codigoCuenta = (fila) =>
  cuentaMask(
    (fila.nivel1 || "") + (fila.nivel2 || "") + (fila.nivel3 || "") +
    (fila.nivel4 || "") + (fila.nivel5 || "")
  );

/**
 * El codigo va como TEXTO explicito (`t: "s"` y `@`): "100-01" no debe acabar
 * convertido en fecha o numero si el usuario edita la celda.
 */
const celdaCodigo = (codigo, tono) => ({
  v: codigo || "",
  t: "s",
  z: "@",
  s: { ...tono, border, numFmt: "@", alignment: alinear("left") },
});

// ── Renglones logicos de una columna del balance ────────────────────────────
// Cada lado (Activo / Pasivo + Capital) se arma como una lista de renglones de
// 3 celdas; `unir` dice cuantas celdas, desde la primera, van combinadas.
const renglonSeccion = (texto) => ({
  celdas: [celdaTexto(texto, bandaSeccionStyle), celdaTexto("", bandaSeccionStyle), celdaTexto("", bandaSeccionStyle)],
  unir: 3,
});

const renglonEncabezado = () => ({
  celdas: ["Cuenta", "Descripción", "Importe"].map((titulo) => celdaTexto(titulo, headerStyle)),
});

const renglonGrupo = (texto) => ({
  celdas: [celdaTexto(texto, tituloGrupoStyle), celdaTexto("", tituloGrupoStyle), celdaTexto("", tituloGrupoStyle)],
  unir: 3,
});

// La sangria es la de Excel (`indent`), no espacios: asi la descripcion se puede
// buscar y filtrar tal cual. Va en la descripcion y no en el codigo, para que los
// codigos queden alineados, como en el PDF.
const renglonCuenta = (fila) => {
  const tono = tonoCuenta(fila);
  return {
    celdas: [
      celdaCodigo(codigoCuenta(fila), tono),
      celdaTexto(fila.descripcion || "", {
        ...tono,
        border,
        alignment: alinear("left", { indent: Math.max((fila.profundidad || 1) - 1, 0) }),
      }),
      celdaImporte(fila.saldo, tono),
    ],
  };
};

/** Renglon de etiqueta (combinada en 2 celdas) + importe, con el tono dado. */
const renglonTotal = (etiqueta, monto, tono) => {
  const estiloEtiqueta = { ...tono, border, alignment: alinear("left") };
  return {
    celdas: [celdaTexto(etiqueta, estiloEtiqueta), celdaTexto("", estiloEtiqueta), celdaImporte(monto, tono)],
    unir: 2,
  };
};

/**
 * Cuerpo de una seccion. Misma regla que `cuerpoSeccion` del PDF: con subtotales
 * las cuentas se reparten por subtipo SAT y cada grupo cierra con el subtotal
 * EXACTO que manda el backend, no con la suma de los renglones visibles (vienen
 * podados por saldo cero y por nivel).
 */
const cuerpoSeccion = (filas, seccion, conSubtotales, analiticas) => {
  const plano = () => filas.map(renglonCuenta);
  if (!conSubtotales || !analiticas) return plano();
  const grupos = agruparPorSubtipo(filas, seccion);
  // Sin grupos (filtro "Nivel 1": todo es cuenta mayor) se cae al arbol plano.
  if (!grupos) return plano();

  const cuerpo = [];
  for (const grupo of grupos) {
    cuerpo.push(renglonGrupo(grupo.titulo));
    grupo.filas.forEach((fila) => cuerpo.push(renglonCuenta(fila)));
    cuerpo.push(
      renglonTotal(grupo.etiquetaTotal, analiticas.subtotales[grupo.llaveTotal], RESALTES.subtotal)
    );
  }
  return cuerpo;
};

/**
 * Suma de una seccion, igual que el PDF clasico: el saldo de las cuentas de nivel 1
 * del arreglo COMPLETO, antes de quitar las de saldo cero, para que esconder un
 * renglon nunca mueva una suma. (La "Utilidad del ejercicio" sintetica es nivel 1.)
 */
const sumaSeccion = (filas) =>
  round2Decimals(
    (filas || []).reduce(
      (total, fila) => (fila && fila.nivel === 1 ? total + (Number(fila.saldo) || 0) : total),
      0
    )
  );

// ── Encabezado del documento ────────────────────────────────────────────────
const textoUnidadDeNegocio = (udn) =>
  cuentaMask(
    (udn.nivel1 || "") + (udn.nivel2 || "") + (udn.nivel3 || "") + (udn.nivel4 || "") + (udn.nivel5 || "")
  ) + " - " + (udn.nombre || "");

export const describirFiltros = (values) => {
  const partes = [];
  if (values.nivel) partes.push(`Nivel ${values.nivel}`);
  if (values.filtrarProyecto === 1 && (values.proyectos || []).length > 0) {
    partes.push(`Proyectos: ${values.proyectos.map((proyecto) => proyecto.nombre).join(", ")}`);
  } else if (values.filtrarProyecto === 2 && values.unidadDeNegocio && values.unidadDeNegocio.nombre) {
    partes.push(`Unidad de negocio: ${textoUnidadDeNegocio(values.unidadDeNegocio)}`);
  } else {
    partes.push("Sin filtros adicionales");
  }
  return partes.join("  |  ");
};

const fechaCorte = (values) => `Al ${moment(values.fechaFinal).locale("es").format("LL")}`;
const fechaImpresion = () => `Fecha de impresión: ${moment().locale("es").format("LL")}`;

// Excel no ajusta el alto de un renglon combinado: con muchos proyectos el texto
// de filtros se cortaria. Se estima cuantas lineas ocupa en el ancho de la hoja.
const altoTextoLargo = (texto, anchoEnCaracteres) =>
  Math.max(1, Math.ceil((texto || "").length / anchoEnCaracteres)) * 13;

/** Ayudante para ir empujando renglones, sus combinaciones y sus altos. */
const nuevaHoja = (totalColumnas) => {
  const filas = [];
  const merges = [];
  const altos = [];
  const ultima = totalColumnas - 1;
  const filaVacia = () => Array.from({ length: totalColumnas }, vacia);
  const unir = (fila, desde, hasta) =>
    merges.push({ s: { r: fila, c: desde }, e: { r: fila, c: hasta } });

  /** Un renglon con una sola celda combinada de `desde` a la ultima columna. */
  const empujarTexto = (texto, estilo, desde = 0, alto) => {
    const fila = filaVacia();
    // Las celdas cubiertas llevan el mismo estilo para que el relleno de una banda
    // no se corte en la combinacion.
    for (let c = desde; c <= ultima; c++) fila[c] = celdaTexto(c === desde ? texto : "", estilo);
    filas.push(fila);
    unir(filas.length - 1, desde, ultima);
    if (alto) altos[filas.length - 1] = { hpt: alto };
  };

  const empujarVacia = () => filas.push(filaVacia());

  /** La hoja ya escrita, con sus anchos, combinaciones y altos. */
  const construir = (anchos) => {
    const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
    hoja["!cols"] = anchos.map((wch) => ({ wch }));
    hoja["!merges"] = merges;
    hoja["!rows"] = Array.from({ length: filas.length }, (_, i) => altos[i] || {});
    return hoja;
  };

  return { filas, altos, unir, empujarTexto, empujarVacia, construir };
};

// ── Hoja 1: el balance ──────────────────────────────────────────────────────
const hojaBalance = (values, tipo) => {
  const empresa = values.empresa || {};
  const analiticas = construirAnaliticas(values);
  const conSubtotales = traeSubtotales(tipo) && !!analiticas;

  const totalActivo = sumaSeccion(values.arrayActivo);
  const totalPasivo = sumaSeccion(values.arrayPasivo);
  const totalCapital = sumaSeccion(values.arrayCapital);
  const totalPasivoMasCapital = round2Decimals(totalPasivo + totalCapital);
  const diferencia = round2Decimals(totalActivo - totalPasivoMasCapital);

  // Igual que el PDF: primero se quitan las cuentas sin saldo (redondeado) y
  // despues se marca el arbol, para que un padre sin hijos visibles sea hoja.
  const filasActivo = marcarJerarquia(filtrarSinSaldo(values.arrayActivo));
  const filasPasivo = marcarJerarquia(filtrarSinSaldo(values.arrayPasivo));
  const filasCapital = marcarJerarquia(filtrarSinSaldo(values.arrayCapital));

  const izquierda = [
    renglonSeccion("ACTIVO"),
    renglonEncabezado(),
    ...cuerpoSeccion(filasActivo, "activo", conSubtotales, analiticas),
    renglonTotal("Suma del Activo", totalActivo, RESALTES.utilidad),
  ];
  const derecha = [
    renglonSeccion("PASIVO"),
    renglonEncabezado(),
    ...cuerpoSeccion(filasPasivo, "pasivo", conSubtotales, analiticas),
    renglonTotal("Suma del Pasivo", totalPasivo, RESALTES.utilidad),
    null,
    // Capital tiene un solo subtipo ("Capital contable"): va plano, con su
    // "Utilidad del ejercicio" sintetica al final, como en el PDF.
    renglonSeccion("CAPITAL"),
    renglonEncabezado(),
    ...filasCapital.map(renglonCuenta),
    renglonTotal("Suma del Capital", totalCapital, RESALTES.utilidad),
  ];

  const h = nuevaHoja(ANCHOS.length);

  // Encabezado: logo en A1 (se inyecta despues), datos de la empresa a su derecha.
  h.empujarTexto(nombreEmpresa(empresa), tituloEmpresaStyle, 1);
  h.empujarTexto("R.F.C: " + (empresa.rfc || ""), datosEmpresaStyle, 1);
  h.empujarTexto(direccionEmpresa(empresa), datosEmpresaStyle, 1);
  h.empujarTexto(contactoEmpresa(empresa), metaStyle, 1);
  for (let i = 0; i < FILAS_ENCABEZADO; i++) h.altos[i] = { hpt: ALTO_ENCABEZADO };
  h.empujarVacia();

  h.empujarTexto("ESTADO DE POSICIÓN FINANCIERA", bandaTituloStyle, 0, 24);
  h.empujarTexto(fechaCorte(values), fechaCorteStyle, 0, 18);
  const filtros = describirFiltros(values);
  h.empujarTexto(filtros, metaStyle, 0, altoTextoLargo(filtros, 170));
  h.empujarTexto(fechaImpresion(), metaStyle);
  h.empujarVacia();

  // Cuerpo: las dos columnas renglon por renglon. Si un lado es mas corto, sus
  // celdas quedan vacias.
  const largo = Math.max(izquierda.length, derecha.length);
  for (let i = 0; i < largo; i++) {
    const renglonIzq = izquierda[i];
    const renglonDer = derecha[i];
    const fila = [
      ...(renglonIzq ? renglonIzq.celdas : tripleVacio()),
      vacia(),
      ...(renglonDer ? renglonDer.celdas : tripleVacio()),
    ];
    h.filas.push(fila);
    const indice = h.filas.length - 1;
    if (renglonIzq && renglonIzq.unir) h.unir(indice, 0, renglonIzq.unir - 1);
    if (renglonDer && renglonDer.unir) h.unir(indice, COL_DERECHA, COL_DERECHA + renglonDer.unir - 1);
    if (renglonIzq && renglonIzq.unir === 3) h.altos[indice] = { hpt: 20 };
    if (renglonDer && renglonDer.unir === 3) h.altos[indice] = { hpt: 20 };
  }

  // Pie: las dos sumas que tienen que cuadrar, frente a frente.
  h.empujarVacia();
  const etiquetaPie = { ...pieStyle, alignment: alinear("left") };
  h.filas.push([
    celdaTexto("Suma del Activo", etiquetaPie),
    celdaTexto("", etiquetaPie),
    celdaImporte(totalActivo, pieStyle),
    vacia(),
    celdaTexto("Suma del Pasivo y Capital", etiquetaPie),
    celdaTexto("", etiquetaPie),
    celdaImporte(totalPasivoMasCapital, pieStyle),
  ]);
  h.unir(h.filas.length - 1, 0, 1);
  h.unir(h.filas.length - 1, COL_DERECHA, COL_DERECHA + 1);
  h.altos[h.filas.length - 1] = { hpt: 20 };

  // El PDF no imprime la diferencia; aqui solo aparece cuando el balance NO cuadra,
  // que es justo cuando hay que verla (cuentas mal clasificadas o polizas
  // descuadradas).
  if (diferencia !== 0) {
    const tonoDiferencia = RESALTES.nota;
    const etiqueta = { ...tonoDiferencia, border, alignment: alinear("left") };
    h.filas.push([
      ...tripleVacio(),
      vacia(),
      celdaTexto("Diferencia", etiqueta),
      celdaTexto("", etiqueta),
      celdaImporte(diferencia, enRojo(tonoDiferencia)),
    ]);
    h.unir(h.filas.length - 1, COL_DERECHA, COL_DERECHA + 1);
  }

  // Sin `!autofilter`: hay subtotales y dos tablas lado a lado, y un filtro haria
  // mentir a las sumas. Sin `!freeze`: xlsx-js-style no lo escribe.
  return h.construir(ANCHOS);
};

// ── Hoja 2: el analisis ─────────────────────────────────────────────────────
// Mismo contenido que `Pdf/anexoAnalisisBalancePdf.js`. La columna B siempre es el
// numero (importe o resultado); la C es el % de integracion o la formula.
const ANCHOS_ANALISIS = [34, 20, 52];

const tituloBloqueStyle = {
  font: { bold: true, sz: 11, color: { rgb: "005F6B" } },
  alignment: { horizontal: "left", vertical: "center" },
};
const notaStyle = { font: { sz: 9, color: { rgb: "8C8C8C" } }, alignment: alinear("left", { wrapText: true }) };

const FORMATO_PORCENTAJE = "0.0%";
const FORMATO_VECES = "0.00";

const estiloAnalisis = (fuerte, alerta) => {
  let tono = fuerte ? RESALTES.subtotal : { font: { sz: 10 } };
  if (alerta) tono = enRojo(tono);
  return tono;
};

/**
 * Valor numerico de una celda del analisis. `null` (divisor en cero) se escribe
 * como "N/A". Los porcentajes llegan en escala 0-100 y se guardan como fraccion,
 * para que la celda sea un porcentaje de verdad en Excel.
 */
const celdaValor = (valor, formato, tono) => {
  const estiloBase = { ...tono, border, alignment: alinear("right") };
  if (valor === null || valor === undefined) return celdaTexto("N/A", estiloBase);
  if (formato === "dinero") return celdaImporte(valor, tono);
  const numero = formato === "porcentaje" ? Number(valor) / 100 : Number(valor);
  const numFmt = formato === "porcentaje" ? FORMATO_PORCENTAJE : FORMATO_VECES;
  return { v: numero, t: "n", z: numFmt, s: { ...estiloBase, numFmt } };
};

const hojaAnalisis = (values, analiticas) => {
  const empresa = values.empresa || {};
  const { subtotales } = analiticas;
  const h = nuevaHoja(ANCHOS_ANALISIS.length);
  const encabezado = (titulos) => h.filas.push(titulos.map((titulo) => celdaTexto(titulo, headerStyle)));
  const texto = (valor, tono, extra = {}) =>
    celdaTexto(valor, { ...tono, border, alignment: alinear("left"), ...extra });

  h.empujarTexto(nombreEmpresa(empresa), tituloEmpresaStyle);
  h.empujarTexto("ANÁLISIS DEL BALANCE", bandaTituloStyle, 0, 24);
  h.empujarTexto(fechaCorte(values), fechaCorteStyle, 0, 18);
  const filtros = describirFiltros(values);
  h.empujarTexto(filtros, metaStyle, 0, altoTextoLargo(filtros, 100));
  h.empujarTexto(fechaImpresion(), metaStyle);
  h.empujarVacia();

  // La Diferencia debe dar cero; si no, hay cuentas mal clasificadas o polizas
  // descuadradas, y sale en rojo.
  h.empujarTexto("CUADRE DEL BALANCE", tituloBloqueStyle, 0, 18);
  h.filas.push([celdaTexto("Concepto", headerStyle), celdaTexto("Importe", headerStyle), vacia()]);
  [
    { etiqueta: "Suma del Activo", valor: subtotales.totalActivo },
    { etiqueta: "Suma del Pasivo y Capital", valor: subtotales.totalPasivoMasCapital },
    { etiqueta: "Diferencia", valor: subtotales.diferencia, alerta: subtotales.diferencia !== 0 },
  ].forEach((renglon) => {
    const tono = estiloAnalisis(true, renglon.alerta);
    h.filas.push([texto(renglon.etiqueta, tono), celdaImporte(renglon.valor, tono), vacia()]);
  });
  h.empujarVacia();

  h.empujarTexto("INTEGRACIÓN DEL BALANCE", tituloBloqueStyle, 0, 18);
  encabezado(["Concepto", "Importe", "% Integración"]);
  renglonesResumen(analiticas).forEach((renglon) => {
    const tono = estiloAnalisis(renglon.fuerte, renglon.alerta);
    h.filas.push([
      texto(renglon.etiqueta, tono),
      celdaImporte(subtotales[renglon.llave], tono),
      celdaValor(analiticas.porcentajes[renglon.llave], "porcentaje", tono),
    ]);
  });
  h.empujarVacia();

  h.empujarTexto("RAZONES FINANCIERAS", tituloBloqueStyle, 0, 18);
  encabezado(["Razón", "Resultado", "Fórmula"]);
  renglonesRazones(analiticas).forEach((renglon) => {
    const tono = estiloAnalisis(true, false);
    h.filas.push([
      texto(renglon.etiqueta, tono),
      celdaValor(renglon.valor, renglon.formato, tono),
      texto(renglon.nota, { font: { sz: 9, color: { rgb: "666666" } } }),
    ]);
  });
  h.empujarVacia();
  h.empujarTexto(
    'Las razones se calculan sobre los saldos al corte indicado. "N/A" significa que el divisor es cero.',
    notaStyle,
    0,
    26
  );

  return h.construir(ANCHOS_ANALISIS);
};

// ── Libro ───────────────────────────────────────────────────────────────────
/**
 * Hojas del libro para la variante elegida (`clasico` / `clasicoSubtotales` /
 * `clasicoAnalisis`). La de analisis solo sale si el backend mando `analiticas`.
 *
 * @param {object} values  `state.dataReporte`: la respuesta de
 *                         `reportesFinancieros/reporteEstadoDePosicionFinanciera`
 * @returns {Array<{nombre, hoja}>}
 */
export const construirHojasBalance = (values, tipo) => {
  const datos = values || {};
  const hojas = [{ nombre: "Balance General", hoja: hojaBalance(datos, tipo) }];
  const analiticas = construirAnaliticas(datos);
  if (traeAnalisis(tipo) && analiticas) {
    hojas.push({ nombre: "Análisis", hoja: hojaAnalisis(datos, analiticas) });
  }
  return hojas;
};

/** Sin diagonales: la fecha es la del corte del balance, no la de hoy. */
export const nombreArchivoBalance = (values) =>
  `Estado_de_Posicion_Financiera_al_${moment((values || {}).fechaFinal).format("YYYY-MM-DD")}.xlsx`;

export const descargarExcelBalance = async (values, tipo) => {
  const libro = construirLibroBlob(construirHojasBalance(values, tipo));
  // El logo se inyecta a mano en el zip (la libreria no escribe imagenes) y solo
  // en la primera hoja. Si no se puede, el Excel baja igual sin el.
  const conLogo = await inyectarLogoEnExcel(libro, values && values.empresa && values.empresa.logo, {
    fila: 0,
    columna: 0,
    ...LOGO,
  });
  saveAs(conLogo || libro, nombreArchivoBalance(values));
};
